import { ActionIcon, Anchor, Center, Drawer, Group, Loader, Stack, Text, Title, Tooltip, Typography } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconHelp } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { type Guide, guidesOn, guideTitle, labels, type Resource, renderGuideText, sectionsFor } from '@tnn/core'
import { findResource } from '@tnn/domain'
import { useTranslation } from '@tnn/i18n'
import { StatusPill, useLinkComponent } from '@tnn/ui'
import { lazy, Suspense, useState } from 'react'
import { appLink } from '../app/links'
import { useAppAbility, useCan } from '../auth/session'
import { ResourceAccess } from './access'
import classes from './guides.module.css'

/**
 * Guides (docs/guides.md) in the apps: the "?" of a page, its drawer, and the guides page of Thananan OS. The text is
 * loaded the first time someone opens one, so it stays out of every app's start.
 */
export function useGuides(enabled = true) {
  return useQuery({
    queryKey: ['guides'],
    enabled,
    queryFn: async () => (await import('@tnn/domain/guides')).guides,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  })
}

/** A guide's title in the reader's language */
export function useGuideTitle() {
  const { t } = useTranslation()
  return (guide: Guide) => guideTitle(guide, { find: findResource, t: (key) => t(key) })
}

/** A guide's page in Thananan OS, from any app */
const guideLink = (guide: Guide) => appLink('portal', `/guides/${guide.key}`)

/** The Markdown renderer, loaded with the first guide opened: it stays out of every app's start too */
const Markdown = lazy(() => import('react-markdown'))

/** Markdown with the apps' own words in place of the tokens, and a link to each guide it names */
function GuideMarkdown({ text, resource, guides }: { text: string; resource?: Resource; guides: readonly Guide[] }) {
  const { t } = useTranslation()
  // The router's link: a guide of Thananan OS opens in place there, from another app it loads Thananan OS
  const Link = useLinkComponent()
  const link = (guide: Guide, title: string) => `[${title}](${guideLink(guide)})`
  return (
    <Typography className={classes.text}>
      <Suspense fallback={<Loader size="xs" />}>
        <Markdown components={{ a: ({ node: _, ...props }) => <Link {...props} /> }}>
          {renderGuideText(text, { resource, find: findResource, guides, t: (key) => t(key), link })}
        </Markdown>
      </Suspense>
    </Typography>
  )
}

/** One guide, with only the sections the reader's permissions give; `guides` all of them, for the ones it names */
export function GuideText({ guide, guides, headed }: { guide: Guide; guides: readonly Guide[]; headed?: boolean }) {
  const { t } = useTranslation()
  const ability = useAppAbility()
  const title = useGuideTitle()
  const resource = findResource(guide.key)
  return (
    <Stack gap="md">
      {headed && <Title order={2}>{title(guide)}</Title>}
      <Text c="dimmed">{renderGuideText(guide.summary, { resource, find: findResource, guides, t: (key) => t(key) })}</Text>
      {sectionsFor(guide, ability).map((section) => (
        <Stack key={section.title} gap={4}>
          <Title order={4}>{section.title}</Title>
          <GuideMarkdown text={section.body} resource={resource} guides={guides} />
        </Stack>
      ))}
    </Stack>
  )
}

/** What the registry knows of a resource, written or not: what the reader may do, its statuses, a document's life */
export function ResourceFacts({ resource }: { resource: Resource }) {
  const { t } = useTranslation()
  const can = useCan()
  const actions = resource.actions.filter((action) => can(action, resource.key))
  // A copy is a new record: whoever may create one may copy
  const duplicates = !!resource.duplicate && can('create', resource.key)
  const statusFields = resource.fieldList.filter((f) => f.tones && f.options && f.name !== 'docstatus')
  const actionLabel = (action: string) =>
    t(labels.action(resource, action), { defaultValue: t(`admin:actions.${action}`, { defaultValue: action }) })
  return (
    <Stack gap="md">
      {actions.length > 0 && (
        <Stack gap={4}>
          <Title order={4}>{t('guides.youMay')}</Title>
          <Text>{[...actions.map(actionLabel), ...(duplicates ? [t('actions.duplicate')] : [])].join(' · ')}</Text>
        </Stack>
      )}
      {resource.submittable && (
        <Stack gap={4}>
          <Title order={4}>{t('guides.lifecycle')}</Title>
          <Text>{t('guides.document')}</Text>
        </Stack>
      )}
      {statusFields.map((field) => (
        <Stack key={field.name} gap={6}>
          <Title order={4}>{statusFields.length > 1 ? t(field.label) : t('guides.statuses')}</Title>
          <Group gap="xs">
            {field.options?.map((value) => (
              <StatusPill
                key={value}
                tone={field.tones?.[value] ?? 'neutral'}
                label={t(labels.option(resource, field.name, value))}
                size="sm"
              />
            ))}
          </Group>
        </Stack>
      ))}
    </Stack>
  )
}

/**
 * The guide of a resource's page, in a drawer at the side (a sheet from the bottom on phones); `guide`: a page of its
 * resource that has a guide of its own (a report's), shown alone
 */
export function GuideDrawer({
  resource,
  guide,
  opened,
  onClose,
}: {
  resource: Resource
  guide?: string
  opened: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const title = useGuideTitle()
  const phone = useMediaQuery('(max-width: 47.99em)')
  // Not before it opens: a page's start never waits for the guides
  const { data: guides } = useGuides(opened)
  const shown = guides ? (guide ? guides.filter((g) => g.key === guide) : guidesOn(guides, resource.key)) : undefined
  const own = guide ? shown?.[0] : undefined
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={`${t('guides.title')} · ${own ? title(own) : t(labels.plural(resource))}`}
      position={phone ? 'bottom' : 'right'}
      size={phone ? undefined : 'md'}
      radius={phone ? 'xl' : undefined}
      styles={phone ? { content: { height: 'auto', maxHeight: '92dvh' } } : undefined}
    >
      {!guides || !shown ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : (
        <Stack gap="xl" pb="md">
          {shown.length === 0 && <Text c="dimmed">{t('guides.notWritten')}</Text>}
          {shown.map((g) => (
            <GuideText key={g.key} guide={g} guides={guides} headed={!guide && g.key !== resource.key} />
          ))}
          {!guide && <ResourceFacts resource={resource} />}
          {!guide && <ResourceAccess resource={resource} />}
          <Anchor href={appLink('portal', '/guides')}>{t('guides.all')}</Anchor>
        </Stack>
      )}
    </Drawer>
  )
}

/** The "?" of a page: its guide, the first time loaded on open */
export function GuideButton({ resource, guide }: { resource: Resource; guide?: string }) {
  const { t } = useTranslation()
  const [opened, setOpened] = useState(false)
  return (
    <>
      <Tooltip label={t('guides.open')}>
        <ActionIcon size={40} onClick={() => setOpened(true)} aria-label={t('guides.open')}>
          <IconHelp size={22} stroke={1.75} />
        </ActionIcon>
      </Tooltip>
      <GuideDrawer resource={resource} guide={guide} opened={opened} onClose={() => setOpened(false)} />
    </>
  )
}
